import { useEffect, useMemo, useRef, useState } from 'react';

import { Box, Input, Skeleton, Stack, Typography } from '@mui/joy';
import { IoSearch } from 'react-icons/io5';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { usePermissionsContext } from '../context/permissionsContext';
import { DEFAULT_QUICK_SEARCHES } from '../pages/insightsHub/components/CategoryQuickSearches';
import SearchResultCard, {
  useArticleCovers,
} from '../pages/insightsHub/components/SearchResultCard';
import { findTopicPage, toTopicPageHit } from '../pages/insightsHub/topicPages';
import { track } from '../services/analytics';
import { EVENT_INSIGHTS_HUB_PAGE, EVNT_research_hub_searched } from '../services/analytics/events';
import { makeIndexedInsightsHubSearchRequest } from '../services/typesense/insightsHub';

export default function InsightsHubSearch() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const isSearchPage = pathname === '/research-hub/search';
  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('q') ?? '');
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const [openResultsDrawer, setOpenResultsDrawer] = useState<boolean>(false);
  const [results, setResults] = useState<Array<any>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { isCompassEnabled, whitelistedProjectTypes } = usePermissionsContext();
  const coverByUrl = useArticleCovers();

  // Topic pages aren't Typesense documents, so they can only reach the list by
  // being prepended — same as InsightsHubSearchResults, so both surfaces agree.
  const displayedResults = useMemo(() => {
    const topic = findTopicPage(searchQuery);
    return topic ? [toTopicPageHit(topic, searchQuery), ...results] : results;
  }, [results, searchQuery]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value.replace(/[^a-zA-Z0-9 ]/g, ''));
    if (!isSearchPage) setOpenResultsDrawer(true);
  };

  const handleMakeSearchQuery = async () => {
    const resultsList = await makeIndexedInsightsHubSearchRequest({
      q: searchQuery,
      projectTypes: isCompassEnabled ? whitelistedProjectTypes : [],
    });
    setResults(resultsList);
    setIsLoading(false);
  };

  useEffect(() => {
    if (isSearchPage) return;
    setIsLoading(true);
    const timer = setTimeout(() => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (searchQuery) next.set('q', searchQuery);
          else next.delete('q');
          return next;
        },
        { replace: true }
      );
      handleMakeSearchQuery();
      track(EVNT_research_hub_searched, EVENT_INSIGHTS_HUB_PAGE, { search_term: searchQuery });
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, isSearchPage]);

  useEffect(() => {
    const q = searchParams.get('q') ?? '';
    setSearchQuery((prev) => (prev === q ? prev : q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current?.contains(event.target)) {
        setOpenResultsDrawer(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const goToSearch = (term: string) => {
    const q = term.trim();
    if (!q) return;
    setOpenResultsDrawer(false);
    navigate(`/research-hub/search?q=${encodeURIComponent(q)}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      goToSearch(searchQuery);
    }
  };

  return (
    <>
      {/* Figma 233:14707 "Modal Overlay" — rgb(18,20,22) @ 25% with an 11px
          Figma background blur (CSS blur is half). Sits below the navbar
          (zIndex 11) so only the page content dims, as on the website. */}
      {openResultsDrawer && (
        <Box
          aria-hidden
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 10,
            backgroundColor: 'rgba(18, 20, 22, 0.25)',
            backdropFilter: 'blur(5.5px)',
          }}
        />
      )}
      {/* This wrapper owns the stacking context so the suggestions sit above
          page content rather than competing with it. */}
      <Box
        position={'relative'}
        ref={searchContainerRef}
        sx={(theme) => ({ zIndex: theme.zIndex.popup })}
      >
        <Input
          startDecorator={<IoSearch size={24} />}
          size='lg'
          color='neutral'
          placeholder='Search our research & insights'
          sx={(theme) => ({
            width: '100%',
            zIndex: 99,
            borderRadius: '100px',
            backgroundColor: theme.palette.background.surface,
            borderColor: theme.palette.border.border,
            fontSize: '16px',
            '--Input-minHeight': '60px',
            '--Input-paddingInline': '24px',
            '--Input-gap': '10px',
            '--Input-decoratorChildHeight': '24px',
            '--Input-placeholderColor': theme.palette.text.secondary,
            '--Input-placeholderOpacity': 1,
          })}
          onChange={handleSearch}
          onFocus={() => !isSearchPage && setOpenResultsDrawer(true)}
          value={searchQuery}
          onKeyDown={handleKeyDown}
        />
        {openResultsDrawer ? (
          <Stack
            position={'absolute'}
            top={'calc(100% + 8px)'}
            width={'100%'}
            maxHeight={'400px'}
            zIndex={100}
            sx={(theme) => {
              return {
                background: theme.palette.background.surface,
                overflowY: 'auto',
                overflowX: 'hidden',
              };
            }}
            borderRadius={'md'}
            boxShadow={'lg'}
          >
            {!searchQuery ? (
              <Stack gap={3} p={5}>
                <Typography
                  fontSize='xl2'
                  fontWeight='md'
                  lineHeight='40px'
                  sx={(theme) => ({ color: theme.palette.text.primary })}
                >
                  Recommended topics
                </Typography>
                <Stack direction='row' flexWrap='wrap' gap={2}>
                  {DEFAULT_QUICK_SEARCHES.map(({ label, query }) => (
                    <Stack
                      key={label}
                      direction='row'
                      alignItems='center'
                      gap={1.25}
                      onClick={() => goToSearch(query ?? label)}
                      sx={(theme) => ({
                        cursor: 'pointer',
                        p: '14px',
                        borderRadius: 'md',
                        border: `1px solid ${theme.palette.border.border}`,
                        backgroundColor: theme.palette.background.surface,
                        '&:hover': { backgroundColor: theme.palette.primary.outlinedHoverBg },
                      })}
                    >
                      <IoSearch size={20} />
                      <Typography
                        fontSize='md'
                        fontWeight='md'
                        lineHeight='142%'
                        sx={(theme) => ({ color: theme.palette.text.primary })}
                      >
                        {label}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            ) : isLoading ? (
              Array(10)
                .fill('')
                .map((_, i) => {
                  return (
                    <Stack
                      key={i}
                      px={2}
                      py={3}
                      width={'100%'}
                      borderBottom={(theme) => `1px solid ${theme.palette.divider}`}
                      sx={{ cursor: 'pointer' }}
                      gap={2}
                    >
                      <Skeleton
                        width={'100%'}
                        animation='wave'
                        height={'24px'}
                        variant='rectangular'
                      />
                      <Stack gap={1}>
                        {Array(2)
                          .fill('')
                          .map((_, i) => {
                            return (
                              <Skeleton
                                key={i}
                                width={'100%'}
                                animation='wave'
                                height={'16px'}
                                variant='rectangular'
                              />
                            );
                          })}
                        <Skeleton
                          width={'60%'}
                          animation='wave'
                          height={'16px'}
                          variant='rectangular'
                        />
                      </Stack>
                    </Stack>
                  );
                })
            ) : displayedResults?.length > 0 ? (
              displayedResults?.map((result) => (
                <Box
                  key={result?.document?.id}
                  px={2}
                  py={1.5}
                  borderBottom={(theme) => `1px solid ${theme.palette.divider}`}
                >
                  <SearchResultCard hit={result} coverByUrl={coverByUrl} />
                </Box>
              ))
            ) : (
              <Box
                width={'100%'}
                height={'100%'}
                display={'flex'}
                flexDirection={'column'}
                alignItems={'center'}
                justifyContent={'center'}
                gap={1}
                padding={4}
              >
                <IoSearch size={56} />
                <Typography level='title-md' color='neutral' textAlign={'center'}>
                  No results found matching your query "{searchQuery}"
                </Typography>
              </Box>
            )}
          </Stack>
        ) : null}
      </Box>
    </>
  );
}
